/**
 * Timezone Utilities
 *
 * Handles UTC ↔ PST/PDT conversions for trading data.
 * All database timestamps are stored in UTC.
 * Display and filtering use Pacific Time (PST/PDT).
 */
/**
 * Helper to parse ISO date string with timezone offset
 * Automatically handles PST (-08:00) vs PDT (-07:00)
 */
function createPacificDate(year, month, day, hour = '00', minute = '00', second = '00') {
    // Try PDT first (UTC-7, summer time)
    const pdtStr = `${year}-${month}-${day}T${hour}:${minute}:${second}-07:00`;
    const pdtDate = new Date(pdtStr);
    // Check if the date in Pacific timezone matches what we expect
    const pdtCheck = pdtDate.toLocaleDateString('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    // If PDT is correct, use it; otherwise use PST
    if (pdtCheck === `${month}/${day}/${year}`) {
        return pdtDate;
    }
    return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}-08:00`);
}
/**
 * Get start of day in Pacific Time (midnight PST/PDT)
 * Returns UTC timestamp for midnight Pacific time
 */
export function getStartOfDayPacific(date) {
    const targetDate = date || new Date();
    // Get Pacific date components
    const pacificStr = targetDate.toLocaleDateString('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const [month, day, year] = pacificStr.split('/');
    // Return midnight Pacific as UTC timestamp
    return createPacificDate(year, month, day);
}
/**
 * Add N Pacific calendar days to a date and return midnight Pacific of the result.
 *
 * "Pacific calendar days" means midnight-to-midnight in America/Los_Angeles,
 * NOT 24 * 60 * 60 * 1000 ms additions. The two differ on DST transition days
 * (spring-forward = 23 hours, fall-back = 25 hours), so naive ms arithmetic
 * produces wrong day boundaries on those edges.
 *
 * Internally: anchor at midnight Pacific of `date`, advance the date components,
 * then re-resolve via createPacificDate so the PST/PDT offset is recomputed
 * for the target day. This guarantees the returned timestamp is always
 * midnight Pacific of the (date + n) calendar day regardless of DST.
 *
 * Used by the Annie daily-cap window calculation:
 * - `getStartOfDayPacific(now)` → start of today (Pacific)
 * - `addDaysPacific(now, 1)` → start of tomorrow (Pacific)
 * - The half-open interval [today, tomorrow) is "trades that happened today."
 */
export function addDaysPacific(date, days) {
    // Anchor at midnight Pacific so we operate on whole calendar days.
    const anchor = getStartOfDayPacific(date);
    // Extract Pacific Y/M/D from the anchor. We can't just add `days * 86400000`
    // ms because DST days are 23 or 25 hours, not 24.
    const pacificStr = anchor.toLocaleDateString('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const [month, day, year] = pacificStr.split('/');
    // Use JS Date arithmetic on a UTC-noon anchor to avoid DST midnight quirks
    // during the addition itself, then re-extract Pacific Y/M/D from the result.
    // (Noon UTC is safely inside the same Pacific calendar day for all offsets.)
    const utcNoon = new Date(`${year}-${month}-${day}T12:00:00Z`);
    utcNoon.setUTCDate(utcNoon.getUTCDate() + days);
    const targetStr = utcNoon.toLocaleDateString('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const [targetMonth, targetDay, targetYear] = targetStr.split('/');
    // createPacificDate handles PST vs PDT selection for the resulting day.
    return createPacificDate(targetYear, targetMonth, targetDay);
}
/**
 * Get start of week in Pacific Time (Monday at midnight)
 * Returns UTC timestamp for Monday midnight Pacific time
 */
export function getStartOfWeekPacific(date) {
    const targetDate = date || new Date();
    // Get Pacific time with weekday
    const pacificParts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        weekday: 'long',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(targetDate);
    const weekday = pacificParts.find(p => p.type === 'weekday')?.value || 'Monday';
    const month = pacificParts.find(p => p.type === 'month')?.value || '01';
    const day = pacificParts.find(p => p.type === 'day')?.value || '01';
    const year = pacificParts.find(p => p.type === 'year')?.value || '2025';
    // Calculate days back to Monday
    const dayMap = {
        Sunday: 6,
        Monday: 0,
        Tuesday: 1,
        Wednesday: 2,
        Thursday: 3,
        Friday: 4,
        Saturday: 5,
    };
    const daysBack = dayMap[weekday] || 0;
    // If it's already Monday, use today
    if (daysBack === 0) {
        return createPacificDate(year, month, day);
    }
    // Calculate Monday's date
    const currentDate = new Date(`${year}-${month}-${day}T12:00:00-08:00`);
    currentDate.setDate(currentDate.getDate() - daysBack);
    // Format Monday's date
    const mondayStr = currentDate.toLocaleDateString('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const [monMonth, monDay, monYear] = mondayStr.split('/');
    return createPacificDate(monYear, monMonth, monDay);
}
/**
 * Get start of month in Pacific Time (1st at midnight)
 * Returns UTC timestamp for 1st of month midnight Pacific time
 */
export function getStartOfMonthPacific(date) {
    const targetDate = date || new Date();
    // Get Pacific month/year
    const pacificStr = targetDate.toLocaleDateString('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const [month, , year] = pacificStr.split('/');
    // Return 1st of month midnight Pacific
    return createPacificDate(year, month, '01');
}
/**
 * Get start of year in Pacific Time (Jan 1 at midnight)
 * Returns UTC timestamp for Jan 1 midnight Pacific time
 */
export function getStartOfYearPacific(date) {
    const targetDate = date || new Date();
    // Get Pacific year
    const year = targetDate.toLocaleDateString('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
    });
    // Return Jan 1 midnight Pacific
    return createPacificDate(year, '01', '01');
}
/**
 * Convert UTC Date to Pacific Time for display
 * This is mainly for backward compatibility
 */
export function utcToPacific(utcDate) {
    if (!utcDate) {
        return new Date();
    }
    const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
    // For display purposes, just return the UTC date
    // The display layer should handle timezone conversion
    return date;
}
/**
 * Get date range for time filter
 * Returns [startDate, endDate] in Pacific Time
 */
export function getTimeRangePacific(timeRange) {
    const now = new Date();
    switch (timeRange) {
        case 'today':
            return { start: getStartOfDayPacific(now), end: now };
        case 'week':
            return { start: getStartOfWeekPacific(now), end: now };
        case 'month':
            return { start: getStartOfMonthPacific(now), end: now };
        case 'year':
            return { start: getStartOfYearPacific(now), end: now };
        case 'all':
            return { start: null, end: now };
        default:
            return { start: null, end: now };
    }
}
/**
 * Format date for display in Pacific Time
 */
export function formatPacificDate(utcDate, format = 'datetime') {
    if (!utcDate) {
        return '-';
    }
    const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
    const options = {
        timeZone: 'America/Los_Angeles',
    };
    switch (format) {
        case 'short':
            options.month = 'short';
            options.day = 'numeric';
            break;
        case 'long':
            options.month = 'long';
            options.day = 'numeric';
            options.year = 'numeric';
            break;
        case 'datetime':
            options.month = 'short';
            options.day = 'numeric';
            options.year = 'numeric';
            options.hour = 'numeric';
            options.minute = '2-digit';
            options.hour12 = true;
            break;
    }
    return new Intl.DateTimeFormat('en-US', options).format(date);
}
